import {
    CreateUserNotificationMethodModel,
    ModifyNotificationSettingsModel,
    ModifyUserNotificationMethodModel,
    NotificationMethods,
    NotificationSettings,
    NotifierOption,
    PartialSubscription,
    Subscriber,
    SubscriberTypes,
    SubscriptionId,
    SubscriptionTypes,
    UserNotification,
    UserNotificationMethodId,
} from "@/api/subscriptions";
import { defineStore } from "pinia";
import { useApi } from "@/api";
import { isDefined } from "@/utils";
import { useAuthStore } from "./auth";
import { ProjectId, ProjectPermission } from "@/api/projects";
import { OrganizationPermission } from "@/api/organizations";
import { TicketId, TicketKey } from "@/api/tickets";
import {
    NotificationConfigurationRequest,
    NotificationConfigurationData,
} from "@/api/notificationConfigurations";
import { UserId } from "@/api/users";
import type * as signalr from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { buildHubConnection, keepConnected, type KeptConnection } from "@/signalr";
import { useDesktopNotifications } from "@/composables/desktopNotifications";
import { useWorkLogsStore } from "./workLogs";
import { onAgentWorkChanged } from "@/queries/agentSessions";
import { refetchAllServerState } from "@/queries/utils";

const { subscriptions: subscriptionApi, notificationConfigurations: notificationConfigurationApi } =
    useApi();

// Per-ticket live-update callbacks, keyed by ticket id. Kept outside pinia state
// so the callbacks aren't made reactive.
const ticketListeners = new Map<TicketId, Set<() => void>>();

let keptConnection: KeptConnection | null = null;

// Per-project support queue callbacks. Same shape as ticketListeners, but keyed by project
// so the queue hears about requests it is not already showing a row for.
const supportListeners = new Map<ProjectId, Set<() => void>>();

// Tickets this client has announced presence on, with its replying flag, so both
// survive a reconnect.
const presenceJoined = new Map<TicketId, boolean>();

export type TicketViewer = {
    userId: UserId;
    replying: boolean;
};

export type SubscriptionStatusRequest = {
    ticketId: string | null;
    projectId: string | null;
};

export type SubscriptionsState = {
    _subscriptions: PartialSubscription[];
    _notifications: UserNotification[] | null;
    _configurations: NotificationConfigurationData;
    _notificationPlugins: NotifierOption[];
    _connection: signalr.HubConnection | null;
    _notificationSettings: NotificationSettings | null;
    ticketPresence: Map<TicketId, TicketViewer[]>;
};

export const useSubscriptionsStore = defineStore("subscriptions", {
    state: (): SubscriptionsState => ({
        _notificationPlugins: [],
        _subscriptions: [],
        _configurations: {
            global: [],
            user: [],
            project: [],
            organization: [],
        },
        _connection: null,
        _notificationSettings: null,
        _notifications: null,
        ticketPresence: new Map(),
    }),
    getters: {
        subscriptions: (state) => state._subscriptions,
        configurations: (state) => state._configurations,
        notifierOptions: (state) => state._notificationPlugins,
        connection: (state) => state._connection as signalr.HubConnection,
        connectionId: (state) =>
            (state._connection as signalr.HubConnection | null)?.connectionId ?? null,
    },
    actions: {
        async load(force: boolean = false) {
            const authStore = useAuthStore();
            if (authStore.user == null || (this.$state._subscriptions.length > 0 && !force)) return;

            const user = authStore.user;
            const projects = Array.from(user.projectPermissions.entries())
                .map((e) => ({ projectId: e[0], perms: e[1] }))
                .filter(
                    (p) =>
                        p.perms.includes(ProjectPermission.CanEditTicketDescription) ||
                        p.perms.includes(ProjectPermission.ProjectAdministrator) ||
                        user.isAdmin
                )
                .map((p) => ({ id: p.projectId, type: SubscriberTypes.Project }));

            const companies = Array.from(user.organizationPermissions.entries())
                .map((e) => ({ organizationId: e[0], perms: e[1] }))
                .filter(
                    (c) =>
                        c.perms.includes(OrganizationPermission.CanCreateProjects) ||
                        c.perms.includes(OrganizationPermission.OrganizationAdministrator) ||
                        user.isAdmin
                )
                .map((c) => ({ id: c.organizationId, type: SubscriberTypes.Organization }));

            const ids: Subscriber[] = [
                ...projects,
                ...companies,
                { id: user.id, type: SubscriberTypes.User },
            ];

            await this.loadSubscriptions(ids);
            await this.loadPlugins();
            // Load before connecting: pushes that arrive while the list is still null
            // would otherwise be dropped.
            await this.loadNotifications();
            await this.createConnection();
        },
        async loadSubscriptions(targets?: Subscriber[], replace: boolean = false) {
            let subs: PartialSubscription[] = [];
            if (isDefined(targets)) subs = await subscriptionApi.getSubscriptions(targets);
            else subs = await subscriptionApi.getSubscriptions([]);
            if (!replace) this.$state._subscriptions = subs;
            else
                subs.forEach((s) => {
                    this.$state._subscriptions.splice(
                        this.$state._subscriptions.findIndex((t) => t.id == s.id),
                        1,
                        s
                    );
                });
        },
        async loadConfigurations(targets: NotificationConfigurationRequest) {
            this.$state._configurations =
                await notificationConfigurationApi.getNotificationConfigurations(targets);
        },
        async loadPlugins() {
            this.$state._notificationPlugins = (await subscriptionApi.getNotifierPlugins()).map(
                (p) => ({
                    ...p,
                    group: p.notificationMethod > (200 as NotificationMethods),
                })
            );
        },
        async loadNotificationSettings() {
            const data = await subscriptionApi.getNotificationSettings();
            this.$state._notificationSettings = data;
        },
        async loadNotifications() {
            const data = await subscriptionApi.getNotifications();
            this.$state._notifications = data;
        },
        async createConnection() {
            if (this.$state._connection) return;
            this.$state._connection = buildHubConnection("/wh/notifications");

            this.$state._connection.on("notification", (data: string) => {
                const model: UserNotification = JSON.parse(data) as UserNotification;
                useDesktopNotifications().showFor(model);
                if (this.$state._notifications === null) {
                    this.$state._notifications = [model];
                    return;
                }
                const item = this.$state._notifications.find((n) => n.id == model.id);
                if (item) item.changeLogs = model.changeLogs;
                else this.$state._notifications.push(model);
            });

            this.$state._connection.on(
                "ticket-changed",
                (payload: { ticketId: TicketId; originConnectionId: string | null }) => {
                    // Skip nudges caused by this client's own change - the UI already
                    // reflects it locally.
                    if (
                        payload.originConnectionId &&
                        payload.originConnectionId === this.$state._connection?.connectionId
                    )
                        return;
                    ticketListeners.get(payload.ticketId)?.forEach((callback) => callback());
                }
            );

            this.$state._connection.on(
                "support-changed",
                (payload: { projectId: ProjectId; originConnectionId: string | null }) => {
                    if (
                        payload.originConnectionId &&
                        payload.originConnectionId === this.$state._connection?.connectionId
                    )
                        return;
                    supportListeners.get(payload.projectId)?.forEach((callback) => callback());
                }
            );

            this.$state._connection.on(
                "work-log-changed",
                (payload: { originConnectionId: string | null }) => {
                    if (
                        payload.originConnectionId &&
                        payload.originConnectionId === this.$state._connection?.connectionId
                    )
                        return;
                    void useWorkLogsStore().revalidate();
                }
            );

            this.$state._connection.on("agent-work-changed", (payload: { ticketId: TicketId }) =>
                onAgentWorkChanged(payload.ticketId)
            );

            this.$state._connection.on(
                "ticket-presence",
                (payload: { ticketId: TicketId; viewers: TicketViewer[] }) => {
                    this.$state.ticketPresence.set(payload.ticketId, payload.viewers);
                }
            );

            // Anything pushed while disconnected was missed and group membership died with the old
            // socket: refetch everything and re-join every watched ticket and presence.
            keptConnection = keepConnected(this.$state._connection, () => {
                refetchAllServerState();
                void useWorkLogsStore().revalidate();
                void this.loadNotifications();
                ticketListeners.forEach((_listeners, ticketId) => {
                    void this.$state._connection?.invoke("SubscribeTicket", ticketId);
                });
                supportListeners.forEach((_listeners, projectId) => {
                    void this.$state._connection?.invoke("SubscribeSupport", projectId);
                });
                presenceJoined.forEach((replying, ticketId) => {
                    void this.$state._connection
                        ?.invoke("JoinTicket", ticketId)
                        .then(() =>
                            replying
                                ? this.$state._connection?.invoke("SetReplying", ticketId, true)
                                : undefined
                        );
                });
            });
            await keptConnection.start();
        },
        async closeConnection() {
            const kept = keptConnection;
            keptConnection = null;
            this.$state._connection = null;
            await kept?.stop();
        },
        async watchTicket(ticketId: TicketId, callback: () => void) {
            let listeners = ticketListeners.get(ticketId);
            if (!listeners) {
                listeners = new Set();
                ticketListeners.set(ticketId, listeners);
            }
            listeners.add(callback);

            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("SubscribeTicket", ticketId);
                } catch {
                    // Re-joined by onreconnected once the socket is back.
                }
            }
        },
        async watchSupport(projectId: ProjectId, callback: () => void) {
            let listeners = supportListeners.get(projectId);
            if (!listeners) {
                listeners = new Set();
                supportListeners.set(projectId, listeners);
            }
            listeners.add(callback);

            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("SubscribeSupport", projectId);
                } catch {
                    // Re-joined by onreconnected once the socket is back.
                }
            }
        },
        async unwatchSupport(projectId: ProjectId, callback: () => void) {
            const listeners = supportListeners.get(projectId);
            if (!listeners) return;
            listeners.delete(callback);
            if (listeners.size > 0) return;

            supportListeners.delete(projectId);
            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("UnsubscribeSupport", projectId);
                } catch {
                    // Connection is gone; nothing to leave.
                }
            }
        },
        async joinTicketPresence(ticketId: TicketId) {
            presenceJoined.set(ticketId, false);

            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("JoinTicket", ticketId);
                } catch {
                    // Re-announced by onreconnected once the socket is back.
                }
            }
        },
        async leaveTicketPresence(ticketId: TicketId) {
            presenceJoined.delete(ticketId);
            this.$state.ticketPresence.delete(ticketId);

            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("LeaveTicket", ticketId);
                } catch {
                    // Connection is gone; the server purges presence on disconnect.
                }
            }
        },
        async setReplying(ticketId: TicketId, replying: boolean) {
            if (!presenceJoined.has(ticketId)) return;
            presenceJoined.set(ticketId, replying);

            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("SetReplying", ticketId, replying);
                } catch {
                    // Re-announced by onreconnected once the socket is back.
                }
            }
        },
        async unwatchTicket(ticketId: TicketId, callback: () => void) {
            const listeners = ticketListeners.get(ticketId);
            if (!listeners) return;
            listeners.delete(callback);
            if (listeners.size > 0) return;

            ticketListeners.delete(ticketId);
            const connection = this.$state._connection;
            if (connection?.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("UnsubscribeTicket", ticketId);
                } catch {
                    // Connection is gone; nothing to leave.
                }
            }
        },
        async subscribe(
            targetType: SubscriptionTypes,
            targetId: ProjectId | TicketId,
            userId?: UserId,
            targetKey?: TicketKey | null
        ) {
            const authStore = useAuthStore();

            const id = await subscriptionApi.subscribe(targetType, targetId, userId);
            if (!userId || userId == authStore.user?.id)
                this.$state._subscriptions.push({
                    id: id,
                    subscriptionType: targetType,
                    targetTicketId: (targetType == SubscriptionTypes.Ticket
                        ? targetId
                        : null) as TicketId | null,
                    targetProjectId: (targetType == SubscriptionTypes.Project
                        ? targetId
                        : null) as ProjectId | null,
                    targetTicketKey: (targetType == SubscriptionTypes.Ticket
                        ? targetKey
                        : null) as TicketKey | null,
                });
            return id;
        },
        async unsubscribe(targetType: SubscriptionTypes, targetId: ProjectId | TicketId) {
            const result = await subscriptionApi.unsubscribe(targetType, targetId);
            if (result)
                this.$state._subscriptions.splice(
                    this.$state._subscriptions.findIndex(
                        (t) =>
                            t.subscriptionType == targetType &&
                            t.targetTicketId ==
                                (targetType == SubscriptionTypes.Ticket ? targetId : null) &&
                            t.targetProjectId ==
                                (targetType == SubscriptionTypes.Project ? targetId : null)
                    ),
                    1
                );
            return result;
        },
        async editNotificationSettings(model: ModifyNotificationSettingsModel) {
            const result = await subscriptionApi.modifyNotificationSettings(model);
            if (result) await this.loadNotificationSettings();
        },
        async removeNotificationMethod(id: UserNotificationMethodId) {
            const result = await subscriptionApi.removeNotificationMethod(id);
            if (result) await this.loadNotificationSettings();
        },
        async addNotificationMethod(model: CreateUserNotificationMethodModel) {
            const result = await subscriptionApi.addNotificationMethod(model);
            if (result) await this.loadNotificationSettings();
        },
        async updateNotificationMethod(model: ModifyUserNotificationMethodModel) {
            const result = await subscriptionApi.updateNotificationMethod(model);
            if (result) await this.loadNotificationSettings();
        },
        async clearNotification(id: SubscriptionId) {
            const result = await subscriptionApi.clearNotification(id);
            this.$state._notifications?.splice(
                this.$state._notifications.findIndex((n) => n.id == id),
                1
            );
            return result;
        },
    },
});
